import { addClearPatientFee } from "helpers/fee";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Row } from "primereact/row";
import { useMemo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";

import { IProviderRemainingAmountRow } from "../PaymentDetails";

const StyledDataTable = styled(DataTable)`
  margin-top: ${sizer(6)};
  width: 100%;

  tr {
    background: var(--gray-50);
  }

  span.p-column-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--fontsize-contents);
    color: var(--color-black-4);
  }
  .p-rowgroup-header {
    display: none;
  }

  td {
    font-weight: var(--font-weight-medium);
    font-size: var(--fontsize-contents);
    color: var(--color-matte-black);
  }
`;

const firstColumnTemplate = (row: IProviderRemainingAmountRow) => {
  return <span className="font-bold">{row.providerName}</span>;
};

const remainingAmountTemplate = (
  row: IProviderRemainingAmountRow,
  onValueChange: any
) => {
  return (
    <InputNumber
      value={centsToDollars(row.totalExpectedTransferAmount)}
      currency="USD"
      locale="en-US"
      mode="currency"
      onValueChange={({ value }) => onValueChange(value, row)}
      minFractionDigits={2}
      maxFractionDigits={2}
      min={0}
      aria-autocomplete="none"
    />
  );
};

const providerPriorityTemplate = (
  row: IProviderRemainingAmountRow,
  onPriorityChange: any
) => {
  return (
    <InputNumber
      value={row.providerPriority}
      onValueChange={({ value }) => onPriorityChange(value, row)}
      min={1}
      max={999}
      showButtons={false}
      aria-autocomplete="none"
    />
  );
};

const footerGroupForPayments = (
  updatedRemainingTotal: {
    total: number;
    subtotal: number;
  },
  initialPaymentPlanRemainingAmount: number,
  hasChanges: boolean,
  isGFE: boolean,
  clearPatientFeePercentage: number | undefined,
  monthlyPaymentInfo: string | null,
  displayRecommendation: string | null,
  selectedPaymentOption: string
) => {
  const currentAmountText = parsePrice(
    centsToDollars(updatedRemainingTotal.total)
  );
  const previousAmountText = parsePrice(
    centsToDollars(initialPaymentPlanRemainingAmount)
  );
  const footerText = hasChanges
    ? `${currentAmountText} (previously ${previousAmountText})`
    : currentAmountText;

  // Show subtotal for non-GFE orders OR GFE orders with clear patient fee
  const shouldShowSubtotal =
    !isGFE ||
    (isGFE && clearPatientFeePercentage && clearPatientFeePercentage > 0);

  return (
    <ColumnGroup>
      {shouldShowSubtotal && (
        <Row>
          <Column
            footer="Subtotal Amount"
            colSpan={3}
            footerStyle={{ textAlign: "right" }}
          />
          <Column
            footer={parsePrice(centsToDollars(updatedRemainingTotal.subtotal))}
          />
        </Row>
      )}
      <Row>
        <Column
          footer={
            clearPatientFeePercentage && clearPatientFeePercentage > 0
              ? `Remaining Amount with fee ${clearPatientFeePercentage}%`
              : "Remaining Amount"
          }
          colSpan={3}
          footerStyle={{ textAlign: "end" }}
        />
        <Column footer={footerText} />
      </Row>
      {monthlyPaymentInfo && (
        <Row>
          <Column
            footer="Monthly payment amount"
            colSpan={3}
            footerStyle={{ textAlign: "end" }}
            className="text-md"
          />
          <Column footer={monthlyPaymentInfo} className="text-md" />
        </Row>
      )}
      {displayRecommendation && selectedPaymentOption !== "setAmount" && (
        <Row>
          <Column
            footer={
              <span className="flex align-items-center justify-content-end gap-2">
                <i className="pi pi-exclamation-triangle" />
                <span className="font-medium">Attention:</span>
              </span>
            }
            colSpan={3}
            footerStyle={{
              textAlign: "right",
              fontSize: "1rem",
              fontWeight: "700",
            }}
            className="text-orange-500"
          />
          <Column
            footer={displayRecommendation}
            footerStyle={{
              fontSize: "0.875rem",
              fontWeight: "500",
            }}
            className="text-orange-700"
          />
        </Row>
      )}
    </ColumnGroup>
  );
};

export function RemainingAmountEditTable({
  remainingAmountBreakdown,
  differenceByProvider,
  setDifferenceByProvider,
  setValuesByProvider,
  valuesByProvider,
  clearFeePercentage,
  clearPatientFeePercentage,
  isGFE,
  initialPaymentPlanRemainingAmount,
  initialPreviousMonthlyPaymentInCents,
  paymentInfoOptions,
  parentRecommendation,
  remainingInstallments,
  calculatePaymentInfo,
  accurateRemainingTotal,
  selectedPaymentOption,
}: {
  remainingAmountBreakdown: IProviderRemainingAmountRow[];
  differenceByProvider: { [key: string]: number };
  setDifferenceByProvider: any;
  setValuesByProvider: any;
  valuesByProvider: { [key: string]: any };
  clearFeePercentage: number;
  clearPatientFeePercentage?: number;
  isGFE: boolean;
  initialPaymentPlanRemainingAmount: number;
  initialPreviousMonthlyPaymentInCents: number | null;
  paymentInfoOptions: {
    selectedPaymentOption: string;
    currentInstallmentAmountInCents: number | null;
  };
  parentRecommendation: string | null;
  remainingInstallments: number | null;
  calculatePaymentInfo: (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean,
    initialPreviousMonthlyPaymentInCents?: number | null,
    options?: {
      selectedPaymentOption?: string;
      currentInstallmentAmountInCents?: number | null;
    }
  ) => {
    monthlyPaymentInfo: string | null;
    recommendationInfo: string | null;
    adjustedInstallments: number | null;
  };
  accurateRemainingTotal?: number | null;
  selectedPaymentOption: string;
}) {
  const { setValue } = useFormContext();
  const handleAmountChange = useCallback(
    (value: number, row: IProviderRemainingAmountRow) => {
      const existingDifference = differenceByProvider[row.providerId] || 0;
      const initialValue = remainingAmountBreakdown.find(
        (provider) => provider.providerId === row.providerId
      )?.totalExpectedTransferAmount;
      const newDifference =
        dollarsToCents(value) - row.totalExpectedTransferAmount;
      setDifferenceByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: existingDifference + newDifference,
      }));
      setValuesByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: {
          ...prev[row.providerId],
          initialValue,
          newValue: dollarsToCents(value),
        },
      }));
    },
    [differenceByProvider]
  );

  const handlePriorityChange = useCallback(
    (value: number | null, row: IProviderRemainingAmountRow) => {
      const initialValue = remainingAmountBreakdown.find(
        (provider) => provider.providerId === row.providerId
      )?.totalExpectedTransferAmount;

      setValuesByProvider((prev: any) => ({
        ...prev,
        [row.providerId]: {
          ...prev[row.providerId],
          initialValue: prev[row.providerId]?.initialValue || initialValue,
          newValue:
            prev[row.providerId]?.newValue || row.totalExpectedTransferAmount,
          providerPriority: value,
        },
      }));
    },
    [remainingAmountBreakdown]
  );

  const updatedRemainingTotal = useMemo(() => {
    const initialRemainingAmount = remainingAmountBreakdown.reduce(
      (sum, provider) => sum + provider.totalExpectedTransferAmount,
      0
    );
    const difference = Object.values(differenceByProvider).reduce(
      (total, amount) => total + amount,
      0
    );
    const subtotal = initialRemainingAmount + difference;

    let total;

    if (isGFE && clearPatientFeePercentage && clearPatientFeePercentage > 0) {
      // Always apply the same rounded fee calculation to the subtotal,
      // regardless of whether there are changes or not.
      // This ensures the displayed total is consistent between editing and viewing.
      const subtotalInDollars = centsToDollars(subtotal);
      const totalInDollars = addClearPatientFee(
        subtotalInDollars,
        clearPatientFeePercentage
      );
      total = dollarsToCents(totalInDollars);
    } else if (!isGFE) {
      // For non-GFE orders, like clear package, add clear fee
      total = subtotal * (1 + clearFeePercentage / 100);
    } else {
      // For GFE orders without clear patient fee
      total = subtotal;
    }

    // if (Math.abs(initialPaymentPlanRemainingAmount - total) <= 1) {
    //   total = initialPaymentPlanRemainingAmount;
    // }

    return { total, subtotal };
  }, [
    remainingAmountBreakdown,
    differenceByProvider,
    isGFE,
    clearFeePercentage,
    clearPatientFeePercentage,
    initialPaymentPlanRemainingAmount,
    accurateRemainingTotal,
  ]);

  const tableData = useMemo(() => {
    return remainingAmountBreakdown.map((provider) => {
      const difference = differenceByProvider[provider.providerId] || 0;
      const valuesByProviderForThis = valuesByProvider[provider.providerId];
      const updatedPriority =
        valuesByProviderForThis?.providerPriority !== undefined
          ? valuesByProviderForThis.providerPriority
          : provider.providerPriority;

      return {
        ...provider,
        totalExpectedTransferAmount:
          provider.totalExpectedTransferAmount + difference,
        providerPriority: updatedPriority,
      };
    });
  }, [remainingAmountBreakdown, differenceByProvider, valuesByProvider]);

  useEffect(() => {
    setValue(
      "paymentPlanRemainingAmount",
      centsToDollars(updatedRemainingTotal.total)
    );
  }, [updatedRemainingTotal]);

  const columns = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        body: (row: any) => firstColumnTemplate(row),
        style: { width: "30%" },
      },
      {
        field: "cptCode",
        header: "CPT Code",
        body: (row: any) => {
          return row.cptCode || "N/A";
        },
        style: { width: "20%" },
      },
      {
        field: "providerPriority",
        header: "Payment Priority",
        body: (row: IProviderRemainingAmountRow) => {
          return providerPriorityTemplate(row, handlePriorityChange);
        },
        style: { width: "20%" },
      },
      {
        field: "totalExpectedTransferAmount",
        header: "Remaining to pay",
        body: (row: IProviderRemainingAmountRow) => {
          return remainingAmountTemplate(row, handleAmountChange);
        },
        style: { width: "30%" },
      },
    ];
  }, [handleAmountChange, handlePriorityChange]);

  const hasChanges = useMemo(() => {
    return Object.values(differenceByProvider).some(
      (difference) => difference !== 0
    );
  }, [differenceByProvider]);

  // State to persist the attention message
  const [persistentTableRecommendation, setPersistentTableRecommendation] =
    useState<string | null>(null);

  // Track the last remaining amount that triggered auto-adjustment
  const [lastAdjustedAmount, setLastAdjustedAmount] = useState<number | null>(
    null
  );

  // Track previous installments to detect manual changes
  const [prevInstallments, setPrevInstallments] = useState<number | null>(
    remainingInstallments
  );

  // Calculate payment info (only once!). When setAmount, duration is derived so we don't require remainingInstallments to be set yet.
  const paymentCalculationResult = useMemo(() => {
    const isSetAmountWithInstallment =
      paymentInfoOptions?.selectedPaymentOption === "setAmount" &&
      paymentInfoOptions?.currentInstallmentAmountInCents != null &&
      paymentInfoOptions.currentInstallmentAmountInCents > 0;
    if (
      !isSetAmountWithInstallment &&
      (!remainingInstallments || remainingInstallments <= 0)
    ) {
      return {
        monthlyPaymentInfo: null,
        recommendationInfo: null,
        adjustedInstallments: null,
      };
    }
    return calculatePaymentInfo(
      updatedRemainingTotal.total,
      initialPaymentPlanRemainingAmount,
      remainingInstallments ?? 0,
      hasChanges,
      initialPreviousMonthlyPaymentInCents,
      paymentInfoOptions
    );
  }, [
    updatedRemainingTotal.total,
    initialPaymentPlanRemainingAmount,
    initialPreviousMonthlyPaymentInCents,
    paymentInfoOptions,
    remainingInstallments,
    hasChanges,
    calculatePaymentInfo,
  ]);

  // Show persistent recommendation (from table amount changes or from parent option switch)
  const displayRecommendation =
    persistentTableRecommendation || parentRecommendation;

  // Track previous payment option to detect switches inside the table
  const [prevTablePaymentOption, setPrevTablePaymentOption] = useState(
    paymentInfoOptions?.selectedPaymentOption
  );

  // Auto-update the Remaining Installments field when remaining amount changes
  // or when switching payment options with existing table changes
  useEffect(() => {
    const amountChanged = updatedRemainingTotal.total !== lastAdjustedAmount;
    const optionChanged =
      paymentInfoOptions?.selectedPaymentOption !== prevTablePaymentOption;
    if (optionChanged) {
      setPrevTablePaymentOption(paymentInfoOptions?.selectedPaymentOption);
    }

    if (
      paymentCalculationResult.recommendationInfo &&
      paymentCalculationResult.adjustedInstallments &&
      (amountChanged || optionChanged)
    ) {
      setPersistentTableRecommendation(
        paymentCalculationResult.recommendationInfo
      );

      if (
        paymentCalculationResult.adjustedInstallments !== remainingInstallments
      ) {
        setLastAdjustedAmount(updatedRemainingTotal.total);
        setPrevInstallments(paymentCalculationResult.adjustedInstallments);
        setValue(
          "paymentPlanDuration",
          paymentCalculationResult.adjustedInstallments
        );
        setValue(
          "paymentPlanDurationFull",
          paymentCalculationResult.adjustedInstallments
        );
      }
    }

    // When switching to setAmount, clear recommendation (setAmount has no duration recommendation)
    if (
      optionChanged &&
      paymentInfoOptions?.selectedPaymentOption === "setAmount"
    ) {
      setPersistentTableRecommendation(null);
    }
  }, [
    paymentCalculationResult,
    updatedRemainingTotal.total,
    remainingInstallments,
    paymentInfoOptions?.selectedPaymentOption,
    setValue,
    lastAdjustedAmount,
    prevTablePaymentOption,
  ]);

  // Hide message when user manually changes installments
  useEffect(() => {
    if (
      prevInstallments !== null &&
      remainingInstallments !== prevInstallments
    ) {
      // Check if this is from our auto-adjustment by comparing with adjustedInstallments
      // If the field matches adjustedInstallments, it's our auto-update, not a manual change
      if (
        paymentCalculationResult.adjustedInstallments &&
        remainingInstallments === paymentCalculationResult.adjustedInstallments
      ) {
        // This is our auto-adjustment, just update prevInstallments
        setPrevInstallments(remainingInstallments);
      } else {
        // User manually changed installments - hide the message
        setPersistentTableRecommendation(null);
        setPrevInstallments(remainingInstallments);
      }
    }
  }, [
    remainingInstallments,
    prevInstallments,
    paymentCalculationResult.adjustedInstallments,
  ]);

  // Reset persistent recommendation and flags when there are no changes
  useEffect(() => {
    if (!hasChanges) {
      setPersistentTableRecommendation(null);
      setLastAdjustedAmount(null);
      setPrevInstallments(remainingInstallments);
    }
  }, [hasChanges, remainingInstallments]);

  return (
    <div className="flex col-12 my-3">
      <StyledDataTable
        style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
        value={tableData}
        emptyMessage="No Remaining Amounts Breakdown"
        footerColumnGroup={footerGroupForPayments(
          updatedRemainingTotal,
          initialPaymentPlanRemainingAmount,
          hasChanges,
          isGFE,
          clearPatientFeePercentage,
          paymentCalculationResult.monthlyPaymentInfo,
          displayRecommendation,
          selectedPaymentOption
        )}
      >
        {columns.map((col: any) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={col?.body}
            style={col?.style}
          />
        ))}
      </StyledDataTable>
    </div>
  );
}
